import {
  LAST_POINT_DOT_RADIUS_PX,
  PULSE_RING_MAX_RADIUS_PX,
  PULSE_RING_WIDTH_PX,
  PULSE_RING_PERIOD_MS,
} from "../constants";

const RING_KEYFRAMES = [
  { transform: `scale(${LAST_POINT_DOT_RADIUS_PX / PULSE_RING_MAX_RADIUS_PX})`, opacity: 1 },
  { transform: "scale(1)", opacity: 0 },
];
const RING_TIMING = { duration: PULSE_RING_PERIOD_MS, iterations: Infinity, easing: "ease-out" };

const createElement = (style) => {
  const element = document.createElement("div");

  Object.assign(element.style, { position: "absolute", left: "0", top: "0", ...style });

  return element;
};

/**
 * Thin ring around the last point of the Line chart that grows from the dot
 * and fades (04-line-area.jpg). It is a small element between the text and
 * the crosshair layers, animated by the browser compositor (Web Animations,
 * transform and opacity only), so the chart frames are not redrawn for it.
 * A clip box of the pane size hides the part over the axes.
 *
 * @param {HTMLElement} container
 * @param {string} color - CSS colour of the ring
 */
export const createPulseRing = (container, color) => {
  const diameter = PULSE_RING_MAX_RADIUS_PX * 2;
  const clip = createElement({ overflow: "hidden", pointerEvents: "none", display: "none" });
  const anchor = createElement({});
  const ring = createElement({
    left: `${-PULSE_RING_MAX_RADIUS_PX}px`,
    top: `${-PULSE_RING_MAX_RADIUS_PX}px`,
    width: `${diameter}px`,
    height: `${diameter}px`,
    boxSizing: "border-box",
    border: `${PULSE_RING_WIDTH_PX}px solid ${color}`,
    borderRadius: "50%",
  });
  // Styles written last, per element: a frame touches the elements only when they change
  const writtenStyles = new Map([
    [clip, {}],
    [anchor, {}],
  ]);
  let animation = null;

  const setStyle = (element, name, value) => {
    const written = writtenStyles.get(element);

    if (written[name] === value) return;

    written[name] = value;
    element.style[name] = value;
  };

  anchor.appendChild(ring);
  clip.appendChild(anchor);
  container.appendChild(clip);

  return {
    /**
     * @param {{ x: number, y: number } | null} point - centre of the dot, CSS px of the pane; null hides the ring
     * @param {{ width: number, height: number }} pane
     */
    update: (point, pane) => {
      const isVisible =
        Boolean(point) && point.x >= 0 && point.x <= pane.width && point.y >= 0 && point.y <= pane.height;

      setStyle(clip, "display", isVisible ? "block" : "none");

      if (!isVisible) {
        animation?.cancel();
        animation = null;
        return;
      }

      setStyle(clip, "width", `${pane.width}px`);
      setStyle(clip, "height", `${pane.height}px`);
      setStyle(anchor, "transform", `translate(${point.x}px, ${point.y}px)`);

      if (!animation) animation = ring.animate(RING_KEYFRAMES, RING_TIMING);
    },

    destroy: () => {
      animation?.cancel();
      clip.remove();
    },
  };
};
